/**
 * DocumentPreviewPane
 * Presentational-only renderer for an uploaded document (image or PDF).
 *
 * Holds no data-fetching logic — the caller supplies the resolved URL/type
 * (typically from the useDocumentActions hook). Used by:
 *   - DocumentPreviewDialog  (pop-up modal preview)
 *   - ApprovalDetailModal    (docked side-pane preview beside contract assessment)
 *
 * Images render as <img>; everything else renders via a native <object> and
 * falls back to a download button when the browser can't render the type.
 *
 * The `height` prop lets a caller size the pane to its container: the dialog
 * passes a fixed 600 (its DialogContent gives it a resolved box), while a
 * docked pane passes '100%' against an explicitly-sized flex parent so the
 * <object> fills the cell instead of collapsing to zero height.
 */

import React from 'react';
import { Box, Button, Typography } from '@mui/material';

export default function DocumentPreviewPane({
  previewUrl,
  previewType,
  previewDocId,
  downloadDocument,
  height = 600,
  minHeight = 500,
  fitToWidth = false,
}) {
  if (!previewUrl) return null;

  const isImage = previewType?.startsWith('image/');

  // For PDFs, opt into the browser viewer's "fit page width" via a PDF Open
  // Parameter fragment (#view=FitH) so the document fills the pane edge-to-edge
  // instead of opening at its default zoom. Only appended when requested and only
  // for non-image content; guarded so we don't clobber an existing fragment.
  const pdfUrl =
    !isImage && fitToWidth && previewUrl && !previewUrl.includes('#')
      ? `${previewUrl}#view=FitH`
      : previewUrl;

  return (
    <Box
      sx={{
        width: '100%',
        height,
        minHeight,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {isImage ? (
        <img
          src={previewUrl}
          alt="Document preview"
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      ) : (
        <object
          data={pdfUrl}
          type={previewType || 'application/pdf'}
          style={{ width: '100%', height: '100%' }}
        >
          <Typography variant="body2" color="text.secondary">
            Unable to preview this file type.{' '}
            <Button size="small" onClick={() => downloadDocument(previewDocId)}>
              Download instead
            </Button>
          </Typography>
        </object>
      )}
    </Box>
  );
}
